/* eslint-disable react-refresh/only-export-components */
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';

import { PlayerId } from '@suntex.fim/api-client';

import { AuthService } from '../auth-service';
import {
  AuthState,
  AuthContext as IAuthContext,
  SignInOptions,
  SignOutOptions,
} from '../types';

/**
 * React context for authentication
 */
const AuthContext = createContext<IAuthContext | undefined>(undefined);

/**
 * Hook to access authentication context
 */
export function useAuthContext(): IAuthContext {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Authentication provider component
 */
export function AuthProvider({
  children,
  authService,
}: {
  children: ReactNode;
  authService: AuthService;
}) {
  const ctx = useProvideContext(authService);
  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
}

function useProvideContext(authService: AuthService): IAuthContext {
  const [state, setState] = useState<AuthState>(authService.getState());

  // useLayoutEffect ensures the subscription is active and state is re-synced
  // before any child useEffect fires. This prevents RequireAuth's Navigate from
  // redirecting when JwtUrlLogin has already set isLoading=true in its own
  // useLayoutEffect (which fires before this one, since children come first).
  useLayoutEffect(() => {
    setState(authService.getState());
    const unsubscribe = authService.subscribe((newState) => {
      setState(newState);
    });

    return unsubscribe;
  }, [authService]);

  const refresh = useCallback(async () => {
    return authService.refresh();
  }, [authService]);
  const signIn = useCallback(
    async (options: SignInOptions) => {
      return authService.signIn(options);
    },
    [authService],
  );
  const signOut = useCallback(
    async (options?: SignOutOptions) => {
      return authService.signOut(options);
    },
    [authService],
  );
  const setPlayerId = useCallback(
    (playerId: PlayerId) => {
      return authService.setPlayerId(playerId);
    },
    [authService],
  );

  return {
    ...state,
    refresh,
    signIn,
    signOut,
    setPlayerId,
  };
}
